/**
 * LevelViewport — the spatial-2d track view (Epic C). Renders the course as ONE continuous
 * critical-path spline through every vertex (gaps shown as pits *below* the path, not breaks),
 * with platforms as ground, fences as obstacles, reversal/collapse as tinted zones, and
 * segments flagged red where the validator errors. Zoom (wheel), pan (drag empty space),
 * click a point/segment to select, and a 3D-software-style X/Y translate gizmo on the
 * selected point (X = position along the track via the incoming segment's span; Y = elevation).
 *
 * NOTE: Canvas 2D. The design's target renderer is Three.js-orthographic (3D-ready), but
 * `three` ships no types and `@types/three` isn't installable offline in this worktree; the
 * component boundary is renderer-agnostic so that swap stays localized.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  compileTraversalGrammar,
  gapDepth,
  type SpatialLevel,
  type LevelFinding,
  type ThreeCsProfile,
  type TraversalGrammar,
} from '@volley/crucible-levels';
import { applyBiome } from './previs-skin';
import type { PrevisSkin, PrevisImages, Appearance } from './previs-skin';

type Seg = NonNullable<SpatialLevel['segments']>[number];

interface Placed {
  seg: Seg;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  bad: boolean;
}

const GIZMO_LEN = 34; // px

function placeSegments(
  level: SpatialLevel,
  findings: LevelFinding[],
): { placed: Placed[]; worldW: number; minY: number; maxY: number } {
  const elev = new Map(level.vertices.map((v) => [v.id, v.elevation]));
  const badVertices = new Set(
    findings.filter((f) => f.severity === 'error' && f.at?.vertexId).map((f) => f.at!.vertexId!),
  );
  const placed: Placed[] = [];
  let x = 0;
  let minY = 0;
  let maxY = 0;
  for (const seg of level.segments ?? []) {
    const y0 = elev.get(seg.fromId) ?? 0;
    const y1 = elev.get(seg.toId) ?? 0;
    const x0 = x;
    const x1 = x + Math.max(0, seg.span);
    placed.push({
      seg,
      x0,
      y0,
      x1,
      y1,
      bad: badVertices.has(seg.toId) || badVertices.has(seg.fromId),
    });
    x = x1;
    minY = Math.min(minY, y0, y1);
    maxY = Math.max(maxY, y0, y1, y1 + (seg.obstacleHeight ?? 0));
  }
  return { placed, worldW: Math.max(x, 1), minY, maxY };
}

/** vertex id -> world x (from the left-to-right segment walk). */
function vertexXMap(placed: Placed[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const p of placed) {
    if (!m.has(p.seg.fromId)) m.set(p.seg.fromId, p.x0);
    m.set(p.seg.toId, p.x1);
  }
  return m;
}

/** Piecewise-linear maps between track `t` (0..1, the entity/terrace axis) and world x
 *  (meters, the segment-walk axis). Built from the vertices' (t, worldX) pairs — the same
 *  anchors the spine is drawn on, so an object at a vertex's t lands exactly on it. */
export function makeTMaps(
  level: SpatialLevel,
  vX: Map<string, number>,
): { tToX: (t: number) => number; xToT: (x: number) => number; tToElev: (t: number) => number } {
  const pts = level.vertices
    .map((v) => ({ t: v.t, x: vX.get(v.id) ?? 0, e: v.elevation }))
    .sort((a, b) => a.t - b.t);
  const lerp = (key: 'x' | 'e', axis: 't' | 'x', q: number): number => {
    if (pts.length === 0) return 0;
    if (q <= pts[0][axis]) return pts[0][key];
    for (let i = 1; i < pts.length; i++) {
      if (q <= pts[i][axis]) {
        const a = pts[i - 1];
        const b = pts[i];
        const denom = b[axis] - a[axis];
        const f = denom === 0 ? 0 : (q - a[axis]) / denom;
        return a[key] + f * (b[key] - a[key]);
      }
    }
    return pts[pts.length - 1][key];
  };
  return {
    tToX: (t) => lerp('x', 't', t),
    tToElev: (t) => lerp('e', 't', t),
    xToT: (x) => {
      // invert the x→t anchor walk
      if (pts.length === 0) return 0;
      if (x <= pts[0].x) return pts[0].t;
      for (let i = 1; i < pts.length; i++) {
        if (x <= pts[i].x) {
          const a = pts[i - 1];
          const b = pts[i];
          const denom = b.x - a.x;
          const f = denom === 0 ? 0 : (x - a.x) / denom;
          return a.t + f * (b.t - a.t);
        }
      }
      return pts[pts.length - 1].t;
    },
  };
}

const COLORS = {
  bg: '#0f1216',
  baseline: 'rgba(255,255,255,0.12)',
  path: '#85b7eb',
  ground: 'rgba(93,202,165,0.16)',
  fence: '#efa027',
  reversal: 'rgba(127,119,221,0.22)',
  collapse: 'rgba(226,75,74,0.18)',
  vertex: '#85b7eb',
  bad: '#e24b4a',
  sel: '#f0c000',
  axisX: '#7f77dd',
  axisY: '#5dcaa5',
  label: 'rgba(255,255,255,0.55)',
};

export type LevelSelection =
  | { type: 'vertex'; id: string }
  | { type: 'segment'; index: number }
  | { type: 'entity'; id: string }
  | { type: 'terrace'; id: string };

type Entity = NonNullable<SpatialLevel['entities']>[number];

/** Marker look per entity role (STRUCTURED — never inferred from the ref string). */
const ROLE_STYLE: Record<
  string,
  { fill: string; shape: 'coin' | 'diamond' | 'triangle' | 'flag' | 'square'; label: string }
> = {
  pickup: { fill: '#f2c94c', shape: 'coin', label: 'pickup' },
  enemy: { fill: '#e24b4a', shape: 'diamond', label: 'enemy' },
  checkpoint: { fill: '#5dcaa5', shape: 'flag', label: 'checkpoint' },
  hazard: { fill: '#efa027', shape: 'triangle', label: 'hazard' },
  prop: { fill: '#9aa4b2', shape: 'square', label: 'prop' },
  exit: { fill: '#5fb0e6', shape: 'flag', label: 'exit' },
};
const roleStyle = (role?: string) => ROLE_STYLE[role ?? 'prop'] ?? ROLE_STYLE.prop;
const TERRACE_COLOR = '#c792ea';

type Cycle = { loopSec: number; activeSec: number; telegraphSec?: number; phase?: number };

/** On/off state of a cyclic hazard at time `now`, matching the game's phase/active model.
 *  Absent `phase` ⇒ arrival-timed: the active window centres on the runner's arrival, so a
 *  previs ghost-run shows the hazard dangerous exactly as the runner reaches it. */
export function cycleState(
  cycle: Cycle,
  arrivalTime: number,
  now: number,
): { active: boolean; telegraph: boolean } {
  const activeFrac = Math.min(1, cycle.activeSec / cycle.loopSec);
  const teleFrac = Math.min(1 - activeFrac, (cycle.telegraphSec ?? 0) / cycle.loopSec);
  const p =
    cycle.phase !== undefined
      ? (((now / cycle.loopSec + cycle.phase) % 1) + 1) % 1
      : ((((now - arrivalTime) / cycle.loopSec + activeFrac / 2) % 1) + 1) % 1;
  const active = p < activeFrac;
  return { active, telegraph: !active && p >= 1 - teleFrac };
}

/** Horizontal offset (m) of a patrolling platform/enemy at time `now`. */
export function patrolDX(range: number, sec: number, now: number): number {
  return (range / 2) * Math.sin((2 * Math.PI * now) / sec);
}

/** Draw an entity marker of the given shape, centered at (cx,cy). Fills + strokes with
 *  the ctx's current styles. */
function drawMarker(
  ctx: CanvasRenderingContext2D,
  shape: string,
  cx: number,
  cy: number,
  s: number,
): void {
  ctx.beginPath();
  switch (shape) {
    case 'coin':
      ctx.arc(cx, cy, s, 0, Math.PI * 2);
      break;
    case 'diamond':
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s, cy);
      ctx.lineTo(cx, cy + s);
      ctx.lineTo(cx - s, cy);
      ctx.closePath();
      break;
    case 'triangle':
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s, cy + s);
      ctx.lineTo(cx - s, cy + s);
      ctx.closePath();
      break;
    case 'flag':
      ctx.rect(cx - 1, cy - s, 2, 2 * s);
      ctx.moveTo(cx + 1, cy - s);
      ctx.lineTo(cx + s + 3, cy - s + 3);
      ctx.lineTo(cx + 1, cy - s + 6);
      ctx.closePath();
      break;
    default: // square (prop)
      ctx.rect(cx - s, cy - s, 2 * s, 2 * s);
      break;
  }
  ctx.fill();
  ctx.stroke();
}

/** Resolve an entity's game-look recipe from the skin: keyed by Content-item ref FIRST
 *  (exact, then case-insensitive), then by role. Null ⇒ the previs uses a schematic marker.
 *  STRUCTURED lookup — the ref/role carry the identity; we never parse the ref string. */
export function resolveAppearance(
  skin: PrevisSkin | null | undefined,
  entity: { ref?: string; role?: string },
): Appearance | null {
  const map = skin?.appearance;
  if (!map) return null;
  if (entity.ref) {
    if (map[entity.ref]) return map[entity.ref];
    const lc = entity.ref.toLowerCase();
    for (const [k, v] of Object.entries(map)) if (k.toLowerCase() === lc) return v;
  }
  if (entity.role && map[entity.role]) return map[entity.role];
  return null;
}

/** Width/height (metres) of a recipe, from its sizeM (number = square, or [w,h]). */
export function appearanceSizeM(app: Appearance, fallback = 0.7): [number, number] {
  const s = app.sizeM;
  if (Array.isArray(s)) return [s[0], s[1]];
  if (typeof s === 'number') return [s, s];
  return [fallback, fallback];
}

/** Smallest on-screen dimension (px) a recipe entity is drawn at, so a metre-scale coin
 *  stays legible when the whole level is fit in view. */
const MIN_ENTITY_PX = 11;

/** On-screen [w,h] px of a recipe at the given zoom, with the legibility floor applied
 *  (aspect preserved). Shared by the drawer and the danger-ring so they stay in sync. */
export function appearanceSizePx(app: Appearance, pxPerM: number): [number, number] {
  const [wM, hM] = appearanceSizeM(app);
  const rawW = wM * pxPerM;
  const rawH = hM * pxPerM;
  // Scale up so the smaller dimension reaches the floor (divide by the ACTUAL min, not
  // max(1,min) — else a sub-1px raw undershoots the floor). Guard the degenerate ≤0 case.
  const minRaw = Math.min(rawW, rawH);
  const k = minRaw > 0 ? Math.max(1, MIN_ENTITY_PX / minRaw) : 1;
  return [rawW * k, rawH * k];
}

/** Paint a recipe's fill into a canvas gradient/solid for a box centred at (cx,cy) of the
 *  given px size. Mirrors the CSS the game draws with. */
function fillStyleFor(
  ctx: CanvasRenderingContext2D,
  fill: NonNullable<Appearance['fill']>,
  cx: number,
  cy: number,
  wPx: number,
  hPx: number,
): string | CanvasGradient {
  if (fill.type === 'solid') return fill.color;
  if (fill.type === 'radial') {
    const gx = cx - wPx / 2 + (fill.cx ?? 0.5) * wPx;
    const gy = cy - hPx / 2 + (fill.cy ?? 0.5) * hPx;
    const r = Math.max(wPx, hPx) / 2;
    const g = ctx.createRadialGradient(gx, gy, r * 0.05, gx, gy, r);
    g.addColorStop(0, fill.inner);
    g.addColorStop(1, fill.outer);
    return g;
  }
  // linear: angle in CSS degrees (0 = upward), default top→bottom. The gradient line spans
  // the box so its corners reach the endpoints (CSS length = |w·sin| + |h·cos|); scaling the
  // unit direction by that length keeps non-axis-aligned angles from clamping early.
  const ang = ((fill.angle ?? 180) * Math.PI) / 180;
  const dirx = Math.sin(ang);
  const diry = -Math.cos(ang);
  const len = Math.abs(wPx * Math.sin(ang)) + Math.abs(hPx * Math.cos(ang));
  const hx = (dirx * len) / 2;
  const hy = (diry * len) / 2;
  const g = ctx.createLinearGradient(cx - hx, cy - hy, cx + hx, cy + hy);
  g.addColorStop(0, fill.from);
  g.addColorStop(1, fill.to);
  return g;
}

/** Draw one entity in the GAME'S look from its recipe (sprite → shape+fill). Returns false
 *  when nothing could be drawn (no decoded sprite AND no shape/fill) so the caller falls back
 *  to the schematic marker. `cy` is the entity's centre; bottom-anchored recipes sit on it. */
export function drawAppearance(
  ctx: CanvasRenderingContext2D,
  app: Appearance,
  images: PrevisImages | null | undefined,
  cx: number,
  cy: number,
  pxPerM: number,
): boolean {
  // Legibility floor applied (see appearanceSizePx): entities are metre-scale, so at
  // fit-zoom a coin would be a few px and its gradient/rim would vanish.
  const [wPx, hPx] = appearanceSizePx(app, pxPerM);
  const sprite = app.sprite ? images?.sprites?.[app.sprite] : undefined;
  if (sprite && sprite.width > 0 && sprite.height > 0) {
    const w = wPx;
    const h = w * (sprite.height / sprite.width);
    const top = app.anchor === 'bottom' ? cy - h : cy - h / 2;
    ctx.drawImage(sprite, cx - w / 2, top, w, h);
    return true;
  }
  if (!app.shape && !app.fill) return false;
  const shape = app.shape ?? 'disc';
  // bottom-anchored recipes sit their base on cy; centred recipes centre on it.
  const gy = app.anchor === 'bottom' ? cy - hPx / 2 : cy;
  if (app.glow) {
    ctx.save();
    ctx.shadowColor = app.glow;
    ctx.shadowBlur = Math.max(6, wPx * 0.6);
  }
  ctx.fillStyle = app.fill ? fillStyleFor(ctx, app.fill, cx, gy, wPx, hPx) : '#9aa4b2';
  const rx = wPx / 2;
  const ry = hPx / 2;
  ctx.beginPath();
  switch (shape) {
    case 'rect':
      ctx.rect(cx - rx, gy - ry, wPx, hPx);
      break;
    case 'roundRect': {
      const r = Math.min(rx, ry) * 0.4;
      ctx.moveTo(cx - rx + r, gy - ry);
      ctx.arcTo(cx + rx, gy - ry, cx + rx, gy + ry, r);
      ctx.arcTo(cx + rx, gy + ry, cx - rx, gy + ry, r);
      ctx.arcTo(cx - rx, gy + ry, cx - rx, gy - ry, r);
      ctx.arcTo(cx - rx, gy - ry, cx + rx, gy - ry, r);
      ctx.closePath();
      break;
    }
    case 'triangle':
      ctx.moveTo(cx, gy - ry);
      ctx.lineTo(cx + rx, gy + ry);
      ctx.lineTo(cx - rx, gy + ry);
      ctx.closePath();
      break;
    case 'diamond':
      ctx.moveTo(cx, gy - ry);
      ctx.lineTo(cx + rx, gy);
      ctx.lineTo(cx, gy + ry);
      ctx.lineTo(cx - rx, gy);
      ctx.closePath();
      break;
    case 'blob':
      ctx.ellipse(cx, gy, rx, ry, 0, 0, Math.PI * 2);
      break;
    default: // disc
      ctx.arc(cx, gy, Math.max(rx, ry), 0, Math.PI * 2);
      break;
  }
  ctx.fill();
  if (app.glow) ctx.restore();
  if (app.stroke) {
    ctx.strokeStyle = app.stroke.color;
    ctx.lineWidth = app.stroke.width;
    ctx.stroke();
  }
  return true;
}

export function LevelViewport({
  level,
  findings,
  selected,
  threeCs,
  onEditVertex,
  onEditVertexSpan,
  onMoveEntity,
  onMoveTerrace,
  onSelect,
  skin,
  images,
}: {
  level: SpatialLevel;
  findings: LevelFinding[];
  selected?: LevelSelection | null;
  /** The autorunner 3Cs this level validates against — lets the previs classify a
   *  gap's pit DEPTH (shallow vs deep) with the same helper the build agents use. */
  threeCs?: ThreeCsProfile | null;
  /** Previs palette (design §19) — game mode renders in the game's look when set. */
  skin?: PrevisSkin | null;
  /** Previs Phase 2 — decoded image layers from the built game (optional). */
  images?: PrevisImages | null;
  /** Y drag → new elevation (m). */
  onEditVertex?: (vertexId: string, elevation: number) => void;
  /** X drag → new span (m) of the segment ENTERING this vertex (moves it along the track). */
  onEditVertexSpan?: (vertexId: string, span: number) => void;
  /** Drag an entity marker → new position (t along track) + elevation (m). */
  onMoveEntity?: (entityId: string, t: number, elevation: number) => void;
  /** Drag a terrace → new t-window + height (m). Body drag translates the window and
   *  sets height; width (tEnd−tStart) is preserved (resize via the inspector). */
  onMoveTerrace?: (terraceId: string, tStart: number, tEnd: number, height: number) => void;
  /** Click a vertex or segment → select it (opens the inspector). */
  onSelect?: (sel: LevelSelection | null) => void;
}): React.ReactElement {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 800, h: 320 });
  const [mode, setMode] = useState<'wire' | 'game'>('wire');
  const view = useRef({ zoom: 20, panX: 40, panY: 200, fitKey: '' });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const dragVertex = useRef<{ id: string; axis: 'x' | 'y' } | null>(null);
  const dragObj = useRef<{ kind: 'entity' | 'terrace'; id: string } | null>(null);
  const downPos = useRef<{ x: number; y: number } | null>(null);
  const movedFar = useRef(false);
  const clockRef = useRef(0); // seconds; advances only in previs (game) mode
  const [, forceDraw] = useState(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setSize({ w: Math.max(200, r.width), h: Math.max(160, r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { placed, worldW, minY, maxY } = placeSegments(level, findings);
  const vX = vertexXMap(placed);
  const { tToX, xToT, tToElev } = makeTMaps(level, vX);
  const entities = level.entities ?? [];
  const terraces = level.terraces ?? [];
  // Auto-run speed drives the previs ghost-run + hazard arrival-timing.
  const char = (threeCs as { character?: { autoRunSpeed?: number; runSpeedMax?: number } } | null)
    ?.character;
  const runSpeed = char?.autoRunSpeed ?? char?.runSpeedMax ?? 6;
  /** World-space Y of an entity. `elevation` is HEIGHT ABOVE THE LOCAL GROUND (matches
   *  the game's coin semantics), so entities ride the terrain — a coin trail follows the
   *  hills, an arc rides above a gap. Absent → sits on the surface (0 above). */
  const entityY = (e: Entity): number => tToElev(e.t) + (e.elevation ?? 0);
  const vertexIndex = new Map(level.vertices.map((v, i) => [v.id, i]));
  // Pit-depth classification uses the same helper the build agents read, so the
  // previs shaft matches the eventual render (deep = wall-kick shaft, shallow = pocket).
  const grammar: TraversalGrammar | null =
    threeCs?.archetype === 'autorunner-2d' ? compileTraversalGrammar(threeCs) : null;

  const fitKey = `${level.vertices.length}:${(level.segments ?? []).length}:${worldW.toFixed(1)}:${size.w}x${size.h}`;
  // Recompute zoom/pan to frame the whole track. Shared by the auto-fit effect and
  // the "fit" button — the button forces it (fitKey guard bypassed).
  const applyFit = useCallback(() => {
    const worldH = Math.max(maxY - minY, 4);
    const zoom = Math.min((size.w - 80) / worldW, (size.h - 90) / worldH, 60);
    view.current = { zoom: Math.max(zoom, 2), panX: 40, panY: size.h - 60 + minY * zoom, fitKey };
    forceDraw((n) => n + 1);
  }, [fitKey, worldW, minY, maxY, size.w, size.h]);
  useEffect(() => {
    if (view.current.fitKey === fitKey) return;
    applyFit();
  }, [fitKey, applyFit]);

  // Previs animation clock: advance a wall-clock only in "game" mode and redraw each
  // frame so cyclic hazards + moving platforms + the ghost-run marker animate. Wire mode
  // stays static (authoring). Respects reduced-motion by holding a single frame.
  useEffect(() => {
    if (mode !== 'game') {
      clockRef.current = 0;
      return;
    }
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const start = performance.now();
    let raf = 0;
    const tick = (): void => {
      clockRef.current = (performance.now() - start) / 1000;
      forceDraw((n) => n + 1);
      if (!reduce) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const { zoom, panX, panY } = view.current;
    const sx = (wx: number) => panX + wx * zoom;
    const sy = (wy: number) => panY - wy * zoom;
    // Ground FILL floor — always the canvas bottom so solid ground reads as solid (a
    // segment fills from its terrain top edge DOWN to here; gaps are skipped → pits).
    // NOTE: skin.render.groundFrac is a camera-framing constant (where elevation-0 sits
    // vertically in the fixed-camera game), NOT a fill boundary — the previs auto-fits the
    // whole track, so applying it here floated the grass with sky beneath it. Left as skin
    // metadata for a future fixed-camera previs; the fill must reach the bottom.
    const groundBottom = size.h;

    // Objects layer (terraces + entities) — drawn on top in BOTH wire and previs modes so
    // the alternate high routes and placed content read the same everywhere.
    const drawObjects = (): void => {
      for (const ter of terraces) {
        const x0 = sx(tToX(ter.tStart));
        const x1 = sx(tToX(ter.tEnd));
        const y = sy(ter.height);
        const w = Math.max(4, x1 - x0);
        const isSel = selected?.type === 'terrace' && selected.id === ter.id;
        // drop posts to the ground so it reads as an OVERHEAD platform
        ctx.strokeStyle = 'rgba(199,146,234,0.35)';
        ctx.setLineDash([3, 4]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x0, y);
        ctx.lineTo(x0, sy(0));
        ctx.moveTo(x1, y);
        ctx.lineTo(x1, sy(0));
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = isSel ? COLORS.sel : TERRACE_COLOR;
        ctx.fillRect(x0, y - 3, w, 6);
        ctx.fillStyle = COLORS.label;
        ctx.font = '10px ui-monospace, monospace';
        ctx.fillText(`▔ ${ter.id} · ${ter.height}m`, x0 + 4, y - 6);
        if (isSel) {
          ctx.strokeStyle = COLORS.sel;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(x0 - 2, y - 5, w + 4, 10);
        }
      }
      const now = clockRef.current; // 0 in wire mode → everything static
      for (const e of entities) {
        let cx = sx(tToX(e.t));
        const cy = sy(entityY(e));
        const st = roleStyle(e.role);
        const isSel = selected?.type === 'entity' && selected.id === e.id;
        let alpha = 1;
        let danger: string | null = null; // active hazard = red, telegraph = orange
        if (now > 0) {
          // moving platform / patrol sweep
          if (e.movement === 'patrol' && e.patrolRange) {
            cx += patrolDX(e.patrolRange, e.patrolSec ?? 3, now) * view.current.zoom;
          }
          // cyclic on/off (arrival-timed to the ghost-run): dangerous / warn / idle
          if (e.cycle) {
            const { active, telegraph } = cycleState(e.cycle, tToX(e.t) / runSpeed, now);
            if (active) danger = '#e24b4a';
            else if (telegraph) danger = '#efa027';
            else alpha = 0.3; // retracted / off
          }
        }
        // GAME mode: draw the entity in the game's look from its recipe (sprite → shape+fill).
        // Danger state becomes an overlay ring so the game look survives; wire mode + any entity
        // without a recipe falls back to the schematic role marker.
        let drew = false;
        if (mode === 'game') {
          const app = resolveAppearance(skin, e);
          if (app) {
            const pxPerM = view.current.zoom;
            ctx.globalAlpha = alpha;
            drew = drawAppearance(ctx, app, images, cx, cy, pxPerM);
            ctx.globalAlpha = 1;
            if (drew && danger) {
              const [wPx, hPx] = appearanceSizePx(app, pxPerM);
              const rr = Math.max(wPx, hPx) / 2 + 4;
              // Ring the entity's VISUAL centre — a bottom-anchored recipe sits its base on cy,
              // so its centre is half a height up (else the ring hugs its feet).
              const ringCy = app.anchor === 'bottom' ? cy - hPx / 2 : cy;
              ctx.strokeStyle = danger;
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.arc(cx, ringCy, rr, 0, Math.PI * 2);
              ctx.stroke();
            }
          }
        }
        if (!drew) {
          ctx.globalAlpha = alpha;
          ctx.fillStyle = isSel ? COLORS.sel : (danger ?? st.fill);
          ctx.strokeStyle = 'rgba(0,0,0,0.45)';
          ctx.lineWidth = 1;
          drawMarker(ctx, st.shape, cx, cy, 6);
          ctx.globalAlpha = 1;
        }
        // Ref labels clutter the game look (a level has dozens of coins) — show them only
        // in wire (editing) mode, plus always on the selected entity.
        if (mode !== 'game' || isSel) {
          ctx.fillStyle = COLORS.label;
          ctx.font = '9px ui-monospace, monospace';
          ctx.fillText(e.ref, cx + 9, cy + 3);
        }
        if (isSel) {
          ctx.strokeStyle = COLORS.sel;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(cx, cy, 10, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
      // Ghost-run marker — a runner sweeping at base speed so you can watch the pacing and
      // whether cyclic hazards fire on arrival (previs mode only).
      if (now > 0 && worldW > 0) {
        const rx = runSpeed * (now % (worldW / runSpeed)); // world metres, looping
        const px = sx(rx);
        const py = sy(tToElev(xToT(rx)));
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, py - 9, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    };

    // ---- GAME (previs) mode: the course in the GAME'S look — palette from the
    // previs skin (design §19) when one exists, Bumble-flavoured defaults otherwise ----
    if (mode === 'game') {
      // Biome (design §20): merge this level's environment overrides over the skin palette so a
      // snowy/cave level renders in its own look; unset fields fall back to the default.
      const pal = applyBiome(
        (skin?.palette ?? {}) as NonNullable<PrevisSkin['palette']>,
        level.biome,
      );
      ctx.fillStyle = pal?.sky ?? '#0e1b2c'; // sky
      ctx.fillRect(0, 0, size.w, size.h);
      // Phase 2: built-game backdrop, cover-fit over the sky fill.
      const bgImg = images?.background;
      if (bgImg && bgImg.width > 0 && bgImg.height > 0) {
        const scale = Math.max(size.w / bgImg.width, size.h / bgImg.height);
        const bw = bgImg.width * scale;
        const bh = bgImg.height * scale;
        ctx.drawImage(bgImg, (size.w - bw) / 2, (size.h - bh) / 2, bw, bh);
      }
      // ground (solid earth under every non-gap segment, grass edge on top);
      // Phase 2: the built game's ground tile as a repeating pattern when present.
      const groundPattern = images?.groundTile
        ? ctx.createPattern(images.groundTile, 'repeat')
        : null;
      for (const p of placed) {
        if (p.seg.kind === 'gap') continue;
        ctx.fillStyle = groundPattern ?? pal?.ground ?? '#3a2d22';
        ctx.beginPath();
        ctx.moveTo(sx(p.x0), sy(p.y0));
        ctx.lineTo(sx(p.x1), sy(p.y1));
        ctx.lineTo(sx(p.x1), groundBottom);
        ctx.lineTo(sx(p.x0), groundBottom);
        ctx.closePath();
        ctx.fill();
        ctx.strokeStyle = pal?.groundEdge ?? '#5dcaa5';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(sx(p.x0), sy(p.y0));
        ctx.lineTo(sx(p.x1), sy(p.y1));
        ctx.stroke();
      }
      // gap pits — OPEN shafts (never filled): the ground loop above SKIPS gaps so the
      // background shows through the mouth. Here we only draw the receding walls so the
      // pit reads as depth, not a solid block. Deep = a tall wall-kickable shaft (accent
      // walls + grip chevrons); shallow = a short pocket. Depth via the shared helper so
      // the previs matches the eventual render.
      for (const p of placed) {
        if (p.seg.kind !== 'gap') continue;
        const deep = (grammar ? gapDepth(p.seg, grammar) : (p.seg.depth ?? 'shallow')) === 'deep';
        const shaftM = deep ? 6 : 2.5;
        const xl = sx(p.x0);
        const xr = sx(p.x1);
        const topL = sy(p.y0);
        const topR = sy(p.y1);
        const botL = Math.min(groundBottom, sy(p.y0 - shaftM));
        const botR = Math.min(groundBottom, sy(p.y1 - shaftM));
        const wallCol = deep ? (pal?.groundEdge ?? '#5dcaa5') : 'rgba(255,255,255,0.30)';
        ctx.strokeStyle = wallCol;
        ctx.lineWidth = deep ? 3 : 1.5;
        ctx.beginPath();
        ctx.moveTo(xl, topL);
        ctx.lineTo(xl, botL);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(xr, topR);
        ctx.lineTo(xr, botR);
        ctx.stroke();
        // shaft floor tick (where a fallen runner bottoms out / respawns)
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(xl, botL);
        ctx.lineTo(xr, botR);
        ctx.stroke();
        if (deep) {
          // upward grip chevrons on both walls — the "you can wall-kick here" cue.
          ctx.strokeStyle = wallCol;
          ctx.lineWidth = 1;
          const rungs = 3;
          for (let r = 1; r <= rungs; r++) {
            const yl = topL + ((botL - topL) * r) / (rungs + 1);
            ctx.beginPath();
            ctx.moveTo(xl, yl);
            ctx.lineTo(xl + 6, yl - 4);
            ctx.stroke();
            const yr = topR + ((botR - topR) * r) / (rungs + 1);
            ctx.beginPath();
            ctx.moveTo(xr, yr);
            ctx.lineTo(xr - 6, yr - 4);
            ctx.stroke();
          }
        }
      }
      // zones + fences
      const hazardTint = (hex: string): string => `${hex}2e`; // ~18% alpha over 6-digit hex
      for (const p of placed) {
        if (p.seg.kind === 'reversal' || p.seg.kind === 'collapse') {
          ctx.fillStyle = pal?.hazard
            ? hazardTint(pal.hazard)
            : p.seg.kind === 'reversal'
              ? 'rgba(127,119,221,0.18)'
              : 'rgba(226,75,74,0.18)';
          ctx.fillRect(sx(p.x0), 0, Math.max(2, (p.x1 - p.x0) * zoom), groundBottom);
        }
        if (p.seg.kind === 'conveyor') {
          // Belt: walkable floor, not a hazard — a subtle mechanical-blue band distinguishes
          // it from plain platform without the reversal/collapse danger read.
          ctx.fillStyle = 'rgba(133,183,235,0.16)';
          ctx.fillRect(sx(p.x0), 0, Math.max(2, (p.x1 - p.x0) * zoom), groundBottom);
        }
        if (p.seg.kind === 'fence') {
          const h = p.seg.obstacleHeight ?? 1;
          const w = Math.max(4, (p.seg.obstacleWidth ?? 0.5) * zoom);
          ctx.fillStyle = pal?.fence ?? '#7a5230';
          ctx.fillRect(sx((p.x0 + p.x1) / 2) - w / 2, sy(p.y0 + h), w, h * zoom);
        }
      }
      // Encounter arena (design §22): a boss/set-piece zone at the END of the track where
      // the auto-run halts and the fight plays. Gold band + boundary + label so it reads as
      // a designed fight, not more running track.
      if (level.encounter) {
        const span = level.encounter.arenaSpan ?? Math.min(worldW, 16);
        const ax0 = sx(Math.max(0, worldW - span));
        const ax1 = sx(worldW);
        ctx.fillStyle = 'rgba(240,192,0,0.10)';
        ctx.fillRect(ax0, 0, Math.max(2, ax1 - ax0), groundBottom);
        ctx.strokeStyle = 'rgba(240,192,0,0.5)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(ax0, 0);
        ctx.lineTo(ax0, groundBottom);
        ctx.stroke();
        ctx.fillStyle = COLORS.label;
        ctx.font = '10px ui-monospace, monospace';
        const phases = level.encounter.phases?.length ?? 0;
        ctx.fillText(
          `⚔ ${level.encounter.ref}${phases > 1 ? ` · ${phases} phases` : ''}`,
          ax0 + 4,
          14,
        );
      }
      // the player characters at the start — count + colors from the skin (solo games
      // get one marker, not Bumble's hardcoded four), joined by a rope when >1
      const startY = level.vertices[0]?.elevation ?? 0;
      const brigade = pal?.players ?? ['#f2c94c', '#2dd4bf', '#f472b6', '#fb923c'];
      const cy = sy(startY) - 9;
      if (brigade.length > 1) {
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        brigade.forEach((_, i) => {
          const cx = sx(0.6 + i * 0.9);
          if (i === 0) ctx.moveTo(cx, cy);
          else ctx.lineTo(cx, cy);
        });
        ctx.stroke();
      }
      const sprite = images?.playerSprite;
      brigade.forEach((color, i) => {
        const cx = sx(0.6 + i * 0.9);
        if (sprite && sprite.width > 0 && sprite.height > 0) {
          // Phase 2: the built game's character sprite, ~1.6m tall, bottom-anchored
          // on the start platform.
          const h = Math.max(14, 1.6 * zoom);
          const w = h * (sprite.width / sprite.height);
          ctx.drawImage(sprite, cx - w / 2, sy(startY) - h, w, h);
        } else {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(cx, cy, 8, 0, Math.PI * 2);
          ctx.fill();
        }
      });
      // finish marker
      const lastX = placed[placed.length - 1]?.x1 ?? 0;
      const lastY = level.vertices[level.vertices.length - 1]?.elevation ?? 0;
      ctx.strokeStyle = '#e2e2e2';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(sx(lastX), sy(lastY));
      ctx.lineTo(sx(lastX), sy(lastY) - 34);
      ctx.stroke();
      ctx.fillStyle = '#efa027';
      ctx.beginPath();
      ctx.moveTo(sx(lastX), sy(lastY) - 34);
      ctx.lineTo(sx(lastX) + 16, sy(lastY) - 28);
      ctx.lineTo(sx(lastX), sy(lastY) - 22);
      ctx.closePath();
      ctx.fill();
      drawObjects();
      return;
    }

    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, size.w, size.h);

    ctx.strokeStyle = COLORS.baseline;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, sy(0));
    ctx.lineTo(size.w, sy(0));
    ctx.stroke();

    // 1) zone tints (reversal / collapse) — full-height bands behind everything
    for (const p of placed) {
      if (p.seg.kind === 'reversal' || p.seg.kind === 'collapse') {
        ctx.fillStyle = p.seg.kind === 'reversal' ? COLORS.reversal : COLORS.collapse;
        ctx.fillRect(sx(p.x0), 0, Math.max(2, (p.x1 - p.x0) * zoom), size.h);
      }
    }

    // 2) ground fill — every segment EXCEPT gap gets solid ground; gaps read as pits
    for (const p of placed) {
      if (p.seg.kind === 'gap') continue;
      ctx.fillStyle = p.bad ? 'rgba(226,75,74,0.12)' : COLORS.ground;
      ctx.beginPath();
      ctx.moveTo(sx(p.x0), sy(p.y0));
      ctx.lineTo(sx(p.x1), sy(p.y1));
      ctx.lineTo(sx(p.x1), groundBottom);
      ctx.lineTo(sx(p.x0), groundBottom);
      ctx.closePath();
      ctx.fill();
    }

    // 3) fences — obstacle rising from the ground line
    for (const p of placed) {
      if (p.seg.kind !== 'fence') continue;
      const h = p.seg.obstacleHeight ?? 1;
      const w = Math.max(3, (p.seg.obstacleWidth ?? 0.4) * zoom);
      ctx.fillStyle = p.bad ? COLORS.bad : COLORS.fence;
      ctx.fillRect(sx((p.x0 + p.x1) / 2) - w / 2, sy(p.y0 + h), w, h * zoom);
    }

    // 4) the CONTINUOUS critical-path spline through every vertex (gaps dashed = jump)
    for (const p of placed) {
      ctx.strokeStyle = p.bad ? COLORS.bad : COLORS.path;
      ctx.lineWidth = p.seg.kind === 'gap' ? 2 : 3;
      ctx.setLineDash(p.seg.kind === 'gap' ? [6, 5] : []);
      ctx.beginPath();
      ctx.moveTo(sx(p.x0), sy(p.y0));
      ctx.lineTo(sx(p.x1), sy(p.y1));
      ctx.stroke();
    }
    ctx.setLineDash([]);

    // 5) selected-segment highlight
    if (selected?.type === 'segment' && placed[selected.index]) {
      const p = placed[selected.index];
      ctx.strokeStyle = COLORS.sel;
      ctx.lineWidth = 2;
      ctx.strokeRect(sx(p.x0), 4, Math.max(3, (p.x1 - p.x0) * zoom), size.h - 8);
    }

    // 6) vertices + labels + selection ring
    for (const v of level.vertices) {
      const wx = vX.get(v.id) ?? 0;
      const isSel = selected?.type === 'vertex' && selected.id === v.id;
      ctx.fillStyle = isSel ? COLORS.sel : COLORS.vertex;
      ctx.beginPath();
      ctx.arc(sx(wx), sy(v.elevation), isSel ? 6 : 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = COLORS.label;
      ctx.font = '10px ui-monospace, monospace';
      ctx.fillText(v.id, sx(wx) + 6, sy(v.elevation) - 6);
      if (v.hazard) {
        ctx.fillStyle = COLORS.fence;
        ctx.fillText(`⚠ ${v.hazard}`, sx(wx) + 6, sy(v.elevation) - 17);
      }
    }

    // 6.5) objects layer — terraces + entities on top of the spine
    drawObjects();

    // 7) translate gizmo on the selected vertex (X = span, Y = elevation)
    if (selected?.type === 'vertex' && (onEditVertex || onEditVertexSpan)) {
      const v = level.vertices.find((x) => x.id === selected.id);
      if (v) {
        const px = sx(vX.get(v.id) ?? 0);
        const py = sy(v.elevation);
        const idx = vertexIndex.get(v.id) ?? 0;
        const arrow = (x2: number, y2: number, color: string) => {
          ctx.strokeStyle = color;
          ctx.fillStyle = color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          const a = Math.atan2(y2 - py, x2 - px);
          ctx.beginPath();
          ctx.moveTo(x2, y2);
          ctx.lineTo(x2 - 7 * Math.cos(a - 0.4), y2 - 7 * Math.sin(a - 0.4));
          ctx.lineTo(x2 - 7 * Math.cos(a + 0.4), y2 - 7 * Math.sin(a + 0.4));
          ctx.closePath();
          ctx.fill();
        };
        if (onEditVertex) arrow(px, py - GIZMO_LEN, COLORS.axisY); // Y = elevation (up)
        if (onEditVertexSpan && idx > 0) arrow(px + GIZMO_LEN, py, COLORS.axisX); // X = span (right)
      }
    }
  }, [
    placed,
    level.vertices,
    level.biome,
    vX,
    vertexIndex,
    terraces,
    entities,
    tToX,
    tToElev,
    xToT,
    entityY,
    worldW,
    runSpeed,
    size.w,
    size.h,
    onEditVertex,
    onEditVertexSpan,
    selected,
    mode,
    skin,
    images,
  ]);

  useEffect(() => {
    draw();
  }, [draw]);

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const v = view.current;
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const newZoom = Math.max(2, Math.min(200, v.zoom * factor));
    v.panX = mx - ((mx - v.panX) * newZoom) / v.zoom;
    v.panY = my - ((my - v.panY) * newZoom) / v.zoom;
    v.zoom = newZoom;
    forceDraw((n) => n + 1);
  };

  const hitVertexId = (mx: number, my: number): string | null => {
    const { zoom, panX, panY } = view.current;
    for (const v of level.vertices) {
      const px = panX + (vX.get(v.id) ?? 0) * zoom;
      const py = panY - v.elevation * zoom;
      if (Math.hypot(mx - px, my - py) <= 10) return v.id;
    }
    return null;
  };
  const hitSegmentIndex = (mx: number): number | null => {
    const { zoom, panX } = view.current;
    const wx = (mx - panX) / zoom;
    for (let i = 0; i < placed.length; i++) if (wx >= placed[i].x0 && wx <= placed[i].x1) return i;
    return null;
  };
  /** Is the cursor on the selected vertex's X or Y gizmo handle? */
  const hitGizmoAxis = (mx: number, my: number): 'x' | 'y' | null => {
    if (selected?.type !== 'vertex') return null;
    const v = level.vertices.find((x) => x.id === selected.id);
    if (!v) return null;
    const { zoom, panX, panY } = view.current;
    const px = panX + (vX.get(v.id) ?? 0) * zoom;
    const py = panY - v.elevation * zoom;
    const idx = vertexIndex.get(v.id) ?? 0;
    if (onEditVertex && Math.abs(mx - px) <= 9 && my <= py + 4 && my >= py - GIZMO_LEN - 8)
      return 'y';
    if (
      onEditVertexSpan &&
      idx > 0 &&
      Math.abs(my - py) <= 9 &&
      mx >= px - 4 &&
      mx <= px + GIZMO_LEN + 8
    )
      return 'x';
    return null;
  };

  /** Cursor over an entity marker → its id (markers draw on top, so they win). */
  const hitEntityId = (mx: number, my: number): string | null => {
    const { zoom, panX, panY } = view.current;
    for (const e of entities) {
      const px = panX + tToX(e.t) * zoom;
      const py = panY - entityY(e) * zoom;
      if (Math.hypot(mx - px, my - py) <= 10) return e.id;
    }
    return null;
  };
  /** Cursor over a terrace bar → its id. */
  const hitTerraceId = (mx: number, my: number): string | null => {
    const { zoom, panX, panY } = view.current;
    for (const ter of terraces) {
      const x0 = panX + tToX(ter.tStart) * zoom;
      const x1 = panX + tToX(ter.tEnd) * zoom;
      const y = panY - ter.height * zoom;
      if (mx >= Math.min(x0, x1) - 3 && mx <= Math.max(x0, x1) + 3 && Math.abs(my - y) <= 6)
        return ter.id;
    }
    return null;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    downPos.current = { x: e.clientX, y: e.clientY };
    movedFar.current = false;
    if (mode === 'game') {
      drag.current = { x: e.clientX, y: e.clientY }; // previs = pan/zoom only
      return;
    }
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const axis = hitGizmoAxis(mx, my);
      if (axis && selected?.type === 'vertex') {
        dragVertex.current = { id: selected.id, axis };
        return;
      }
      // objects layer wins over the spine (markers draw on top)
      const eid = onMoveEntity ? hitEntityId(mx, my) : null;
      if (eid) {
        dragObj.current = { kind: 'entity', id: eid };
        return;
      }
      const tid = onMoveTerrace ? hitTerraceId(mx, my) : null;
      if (tid) {
        dragObj.current = { kind: 'terrace', id: tid };
        return;
      }
      const vid = onEditVertex ? hitVertexId(mx, my) : null;
      if (vid) {
        dragVertex.current = { id: vid, axis: 'y' }; // body drag = elevation
        return;
      }
    }
    drag.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (
      downPos.current &&
      Math.hypot(e.clientX - downPos.current.x, e.clientY - downPos.current.y) > 4
    ) {
      movedFar.current = true;
    }
    const dv = dragVertex.current;
    if (dv && movedFar.current) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { zoom, panX, panY } = view.current;
      if (dv.axis === 'y' && onEditVertex) {
        const elevation = Math.round(((panY - (e.clientY - rect.top)) / zoom) * 10) / 10;
        onEditVertex(dv.id, elevation);
      } else if (dv.axis === 'x' && onEditVertexSpan) {
        const idx = vertexIndex.get(dv.id) ?? 0;
        const prevId = level.vertices[idx - 1]?.id;
        const prevX = prevId ? (vX.get(prevId) ?? 0) : 0;
        const worldX = (e.clientX - rect.left - panX) / zoom;
        onEditVertexSpan(dv.id, Math.max(0, Math.round((worldX - prevX) * 10) / 10));
      }
      return;
    }
    const dobj = dragObj.current;
    if (dobj && movedFar.current) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { zoom, panX, panY } = view.current;
      const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
      const r3 = (n: number) => Math.round(n * 1000) / 1000;
      const r1 = (n: number) => Math.round(n * 10) / 10;
      const wx = (e.clientX - rect.left - panX) / zoom;
      const wy = (panY - (e.clientY - rect.top)) / zoom;
      if (dobj.kind === 'entity' && onMoveEntity) {
        const t = clamp01(r3(xToT(wx)));
        // elevation is height ABOVE the local ground, so subtract the terrain at t.
        onMoveEntity(dobj.id, t, Math.max(0, r1(wy - tToElev(t))));
      } else if (dobj.kind === 'terrace' && onMoveTerrace) {
        const ter = terraces.find((x) => x.id === dobj.id);
        if (ter) {
          const width = ter.tEnd - ter.tStart;
          let tStart = clamp01(xToT(wx) - width / 2);
          let tEnd = tStart + width;
          if (tEnd > 1) {
            tEnd = 1;
            tStart = Math.max(0, 1 - width);
          }
          onMoveTerrace(dobj.id, r3(tStart), r3(tEnd), Math.max(0.1, r1(wy)));
        }
      }
      return;
    }
    if (!drag.current) return;
    view.current.panX += e.clientX - drag.current.x;
    view.current.panY += e.clientY - drag.current.y;
    drag.current = { x: e.clientX, y: e.clientY };
    forceDraw((n) => n + 1);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (mode !== 'game' && !movedFar.current && onSelect) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        // objects first (they draw on top), then the spine
        const eid = hitEntityId(mx, my);
        const tid = eid ? null : hitTerraceId(mx, my);
        const vid = eid || tid ? null : hitVertexId(mx, my);
        if (eid) onSelect({ type: 'entity', id: eid });
        else if (tid) onSelect({ type: 'terrace', id: tid });
        else if (vid) onSelect({ type: 'vertex', id: vid });
        else {
          const si = hitSegmentIndex(mx);
          onSelect(si === null ? null : { type: 'segment', index: si });
        }
      }
    } else if (dragVertex.current && onSelect) {
      onSelect({ type: 'vertex', id: dragVertex.current.id });
    } else if (dragObj.current && onSelect) {
      onSelect(
        dragObj.current.kind === 'entity'
          ? { type: 'entity', id: dragObj.current.id }
          : { type: 'terrace', id: dragObj.current.id },
      );
    }
    drag.current = null;
    dragVertex.current = null;
    dragObj.current = null;
    downPos.current = null;
  };

  const resetView = () => applyFit();

  return (
    <div
      ref={wrapRef}
      className="relative min-h-[300px] flex-1 overflow-hidden rounded-lg border border-crucible-border"
    >
      <canvas
        ref={canvasRef}
        style={{
          width: size.w,
          height: size.h,
          display: 'block',
          cursor: drag.current ? 'grabbing' : 'crosshair',
        }}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
      <div className="absolute left-2 top-2 flex overflow-hidden rounded border border-crucible-border text-[10px]">
        {(['wire', 'game'] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-2 py-0.5 ${
              mode === m
                ? 'bg-crucible-accent/20 text-crucible-fg'
                : 'bg-crucible-bg-elevated text-crucible-muted hover:text-crucible-fg'
            }`}
          >
            {m === 'wire' ? 'Wire' : 'Previs'}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={resetView}
        className="absolute right-2 top-2 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-0.5 text-[10px] text-crucible-muted hover:text-crucible-fg"
      >
        fit
      </button>
      <div className="pointer-events-none absolute bottom-2 left-2 text-[10px] text-crucible-muted">
        {mode === 'game'
          ? 'previs · scroll = zoom · drag = pan'
          : 'scroll = zoom · drag empty = pan · click point/segment = select · drag handles = move'}
      </div>
    </div>
  );
}
